import { HttpClient } from '@angular/common/http';
import { inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject, finalize, from, Observable, switchMap } from 'rxjs';
import { EnvironmentToken } from '../../app.config';
import { EyeCatcher } from '../models/eye-catcher.model';

export class EyeCatcherService {
  #http = inject(HttpClient);
  #apiUrl = inject(EnvironmentToken).apiUrl;
  #isLoading = signal<Set<unknown>>(new Set());

  #eyeCatchers = new BehaviorSubject<EyeCatcher[]>([]);

  public eyeCatchers$ = this.#eyeCatchers.asObservable();
  public eyeCatchers = toSignal(this.eyeCatchers$, {
    initialValue: [],
  });

  getEyeCatchers() {
    this.#execute(
      this.#http.get<EyeCatcher[]>(
        `${this.#apiUrl}/api/parcour/eyeCatchers`,
      ),
    ).subscribe({
      next: (eyeCatcher: any) => this.#eyeCatchers.next(eyeCatcher),
      error: (e: any) => console.log(e),
    });
  }

  updateEyeCatcher(data: any, file: File | undefined, id: number) {
    const payload = this.#buildPayload(data);
    if (!file) {
      return this.#execute(
        this.#http.put<EyeCatcher[]>(
          `${this.#apiUrl}/api/parcour/eyeCatchers/` + id,
          payload,
        ),
      );
    }
    return this.#execute(
      from(this.#fileToBase64(file)).pipe(
        switchMap((base64) =>
          this.#http.put<EyeCatcher[]>(
            `${this.#apiUrl}/api/parcour/eyeCatchers/` + id,
            { ...payload, image: base64 },
          ),
        ),
      ),
    );
  }

  createEyeCatcher(data: any, file: File) {
    const payload = this.#buildPayload(data);
    return this.#execute(
      from(this.#fileToBase64(file)).pipe(
        switchMap((base64) =>
          this.#http.post<EyeCatcher[]>(
            `${this.#apiUrl}/api/parcour/eyeCatchers`,
            { ...payload, image: base64 },
          ),
        ),
      ),
    );
  }

  deleteEyeCatcher(id: number) {
    return this.#execute(
      this.#http.delete(
        `${this.#apiUrl}/api/parcour/eyeCatchers/` + id,
      ),
    );
  }

  #buildPayload(data: any): Record<string, unknown> {
    const validDates: Date[] | null = data.valid_dates;
    return {
      url: data.url,
      valid_from: validDates?.[0]?.toISOString() ?? null,
      valid_until: validDates?.[1]?.toISOString() ?? null,
    };
  }

  #fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  #execute<T = unknown>(apiCall: Observable<T>) {
    this.#isLoading.update((x) => {
      x.add(apiCall);
      return new Set(x.values());
    });
    return apiCall.pipe(
      finalize(() =>
        this.#isLoading.update((x) => {
          x.delete(apiCall);
          return new Set(x.values());
        }),
      ),
    );
  }
}
